"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import TaxonomyPicker from "@/components/TaxonomyPicker";
import AgentStatusBadge from "@/components/AgentStatusBadge";
import { ROLE_VALUES, Role, isValidRole } from "@/lib/listings/roles";

interface SearchResult {
  id: string;
  display_name: string | null;
  roles: Role[];
  one_liner: string | null;
  collab_interest: string | null;
  final_score: number;
  index_status: string;
  agent_status: "online" | "offline";
  agent_connection: "realtime" | "polling" | null;
  listing_kind: "agent" | "form";
}

export default function SearchPage() {
  const t = useTranslations("search");
  const tr = useTranslations("roles");
  const tc = useTranslations("common");
  const router = useRouter();
  const urlParams = useSearchParams();

  const [query, setQuery] = useState(() => urlParams.get("q") ?? "");
  const [categoryId, setCategoryId] = useState<string | null>(() => urlParams.get("category_id"));
  const [regionId, setRegionId] = useState<string | null>(() => urlParams.get("region_id"));
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(
    () => urlParams.get("roles")?.split(",").filter(isValidRole) ?? []
  );
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [degraded, setDegraded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleRole(role: Role) {
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  }

  const hasAnyCriteria = query.trim().length > 0 || categoryId !== null || regionId !== null || selectedRoles.length > 0;

  /**
   * 실사용 중 발견: 검색 → 리스팅 클릭 → 뒤로가기를 하면 검색어/필터/결과가 전부 초기화돼 있었다
   * (전부 순수 useState라 URL에 아무 흔적이 없었음) — 검색 조건을 URL 쿼리스트링에 반영해서
   * 뒤로가기 시 브라우저가 그 URL로 돌아오면 같은 조건으로 자동 재검색되게 한다.
   */
  async function runSearch() {
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (categoryId) params.set("category_id", categoryId);
    if (regionId) params.set("region_id", regionId);
    if (selectedRoles.length > 0) params.set("roles", selectedRoles.join(","));
    router.replace(params.toString() ? `/search?${params.toString()}` : "/search");

    if (!hasAnyCriteria) {
      // 검색어/필터가 하나도 없으면 "전체 브라우징"을 하지 않는다 — 아무 조건 없이 검색을
      // 눌렀을 때 활성 리스팅 전부가 쏟아지는 건 사용자가 기대하는 동작이 아니다.
      setResults([]);
      setDegraded(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/directory?${params.toString()}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message ?? tc("unknownError"));
      setResults(json.results);
      setDegraded(json.degraded);
    } catch (e) {
      setError(e instanceof Error ? e.message : tc("unknownError"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // 마운트 시 URL에 남아있던 조건(뒤로가기로 복귀한 경우)으로 한 번만 자동 재검색한다 — 이후는
    // 사용자 액션(버튼/Enter)으로만 재검색. runSearch는 버튼 클릭 핸들러와 동일한 함수를 공유해야
    // 해서 effect 전용 IIFE로 분리할 수 없다(다른 페이지들의 관례와 다른 이유).
    if (query.trim() || categoryId || regionId || selectedRoles.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 px-6 py-16">
      <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-50">{t("title")}</h1>

      <div className="flex flex-col gap-4">
        <div className="flex gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder={t("queryPlaceholder")}
            className="flex-1 rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          />
          <button
            onClick={() => runSearch()}
            disabled={loading}
            className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50 dark:bg-zinc-50 dark:text-zinc-900"
          >
            {loading ? tc("loading") : t("searchButton")}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TaxonomyPicker kind="category" label={t("categoryFilter")} onSelect={(id) => setCategoryId(id)} />
          <TaxonomyPicker kind="region" label={t("regionFilter")} onSelect={(id) => setRegionId(id)} />
        </div>

        <div className="flex flex-wrap gap-2">
          {ROLE_VALUES.map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => toggleRole(role)}
              className={
                "rounded-full border px-3 py-1.5 text-xs " +
                (selectedRoles.includes(role)
                  ? "border-zinc-900 bg-zinc-900 text-white dark:border-zinc-50 dark:bg-zinc-50 dark:text-zinc-900"
                  : "border-zinc-300 text-zinc-700 dark:border-zinc-700 dark:text-zinc-300")
              }
            >
              {tr(role)}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {degraded && (
        <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          {t("degradedNotice")}
        </p>
      )}

      {results && (
        <div className="flex flex-col gap-3">
          {results.length === 0 && <p className="text-sm text-zinc-500">{hasAnyCriteria ? t("empty") : t("noCriteria")}</p>}
          {results.map((r) => (
            <Link
              key={r.id}
              href={`/listings/${r.id}`}
              className="flex flex-col gap-1 rounded-lg border border-zinc-200 p-4 hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-zinc-900 dark:text-zinc-50">{r.display_name ?? "—"}</span>
                <div className="flex items-center gap-1">
                  {r.index_status !== "indexed" && (
                    <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-500 dark:bg-zinc-800">
                      {t("indexingPending")}
                    </span>
                  )}
                  <AgentStatusBadge status={r.listing_kind === "form" ? "form" : r.agent_status} connection={r.agent_connection} />
                </div>
              </div>
              <p className="text-sm text-zinc-700 dark:text-zinc-300">{r.one_liner}</p>
              <div className="flex flex-wrap gap-1 text-xs text-zinc-500">
                {r.roles.map((role) => (
                  <span key={role} className="rounded bg-zinc-100 px-2 py-0.5 dark:bg-zinc-800">
                    {tr(role)}
                  </span>
                ))}
              </div>
            </Link>
          ))}
          {results.some((r) => r.listing_kind === "form") && (
            <p className="px-1 text-xs text-zinc-400 dark:text-zinc-600">{t("formKindNote")}</p>
          )}
        </div>
      )}
    </div>
  );
}
